/**
 * Global registry for tracking "needs_review" indicators across all sections
 * Used to enable navigation between unviewed indicators via the "Next" button
 */

export type IndicatorType = 'bracket' | 'attestation';

export interface IndicatorMetadata {
  id: string;
  type: IndicatorType;
  element: HTMLElement;
  sectionId: string;
  /**
   * Document position for ordering indicators by their position in the document
   * Calculated as: sectionOrder * 1000 + positionInSection
   */
  documentPosition: number;
  /**
   * Optional callback to trigger the indicator's action (e.g., show popover/modal)
   */
  onActivate?: () => void;
  /**
   * Optional callback to close/deactivate the indicator's popover/modal
   */
  onDeactivate?: () => void;
}

class IndicatorRegistry {
  private indicators: Map<string, IndicatorMetadata> = new Map();
  private listeners: Set<() => void> = new Set();

  /**
   * Register a new indicator
   */
  register(metadata: IndicatorMetadata): void {
    this.indicators.set(metadata.id, metadata);
    this.notifyListeners();
  }

  /**
   * Unregister an indicator
   */
  unregister(id: string): void {
    this.indicators.delete(id);
    this.notifyListeners();
  }

  /**
   * Unregister all indicators for a specific section
   */
  unregisterSection(sectionId: string): void {
    const toRemove: string[] = [];
    this.indicators.forEach((metadata, id) => {
      if (metadata.sectionId === sectionId) {
        toRemove.push(id);
      }
    });
    toRemove.forEach(id => this.indicators.delete(id));
    this.notifyListeners();
  }

  /**
   * Get all registered indicators
   */
  getAll(): IndicatorMetadata[] {
    return Array.from(this.indicators.values());
  }

  /**
   * Get all unviewed indicators (filtered by viewed set), sorted by document position
   */
  getUnviewed(viewedIds: Set<string>): IndicatorMetadata[] {
    return this.getAll()
      .filter(indicator => !viewedIds.has(indicator.id))
      .sort((a, b) => a.documentPosition - b.documentPosition);
  }

  /**
   * Get indicator by ID
   */
  getById(id: string): IndicatorMetadata | undefined {
    return this.indicators.get(id);
  }

  /**
   * Subscribe to registry changes (for components that need to react to changes)
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Recalculate document positions when sections are reordered
   * @param sectionOrderMap Map of sectionId to new section order (0-based index)
   */
  recalculatePositions(sectionOrderMap: Map<string, number>): void {
    let updated = false;
    this.indicators.forEach((metadata, id) => {
      const newSectionOrder = sectionOrderMap.get(metadata.sectionId);
      if (newSectionOrder !== undefined) {
        // Extract positionInSection from current documentPosition
        const positionInSection = metadata.documentPosition % 1000;
        const newDocumentPosition = newSectionOrder * 1000 + positionInSection;
        if (newDocumentPosition !== metadata.documentPosition) {
          metadata.documentPosition = newDocumentPosition;
          updated = true;
        }
      }
    });

    if (updated) {
      this.notifyListeners();
    }
  }

  /**
   * Clear all indicators (useful for cleanup)
   */
  clear(): void {
    this.indicators.clear();
    this.notifyListeners();
  }
}

// Export singleton instance
export const indicatorRegistry = new IndicatorRegistry();

